import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { formatDateTime } from "../../lib/format";

export function NotificationBell() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);

    const refreshCount = useCallback(() => {
        api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread_count || 0)).catch(() => {});
    }, []);
    useEffect(() => {
        refreshCount();
        const t = setInterval(refreshCount, 60000);
        return () => clearInterval(t);
    }, [refreshCount]);
    useEffect(() => {
        if (!open) return;
        api.get("/notifications", { params: { limit: 20 } }).then((r) => { setItems(r.data.items || []); setUnread(r.data.unread_count || 0); }).catch(() => {});
    }, [open]);

    const clickItem = async (n) => {
        if (!n.read_at) await api.post(`/notifications/${n.id}/read`).catch(() => {});
        setOpen(false);
        refreshCount();
        if (n.link) navigate(n.link);
    };
    const readAll = async () => {
        await api.post("/notifications/read-all").catch(() => {});
        setItems((xs) => xs.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
        setUnread(0);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" className="relative h-9 w-9 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Notifications" data-testid="notification-bell">
                    <Bell size={19} />
                    {unread > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-unread-count">{unread > 99 ? "99+" : unread}</span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
                <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                    <span className="text-sm font-semibold">Notifications</span>
                    {unread > 0 && <button type="button" className="text-xs text-indigo-700 hover:underline" onClick={readAll} data-testid="notification-read-all">Mark all read</button>}
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                    {items.length === 0 && <div className="px-3 py-8 text-center text-xs text-slate-400" data-testid="notification-empty">You&apos;re all caught up.</div>}
                    {items.map((n) => (
                        <button type="button" key={n.id} onClick={() => clickItem(n)} className={`w-full text-left px-3 py-2.5 border-b border-slate-50 hover:bg-slate-50 ${n.read_at ? "" : "bg-indigo-50/50"}`} data-testid={`notification-item-${n.id}`}>
                            <div className="flex items-start gap-2">
                                {!n.read_at && <span className="mt-1.5 h-2 w-2 rounded-full bg-indigo-600 shrink-0" />}
                                <div className="min-w-0">
                                    <div className="text-xs font-semibold text-slate-900">{n.title}</div>
                                    <div className="text-xs text-slate-600 whitespace-pre-line line-clamp-3">{n.body}</div>
                                    <div className="text-[10px] text-slate-400 mt-0.5">{formatDateTime(n.created_at)}</div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default NotificationBell;
